import { useState } from "react";
import { Headphones, Sparkles } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import LearnReadingListeningRecording from "./LearnReadingListeningRecording";
import LearnSightVocabulary from "./LearnSightVocabulary";
import { newWordsInAyah } from "../recordingRender";

// New (not-yet-covered-in-an-earlier-module) words for just the currently
// selected ayah/group — not the whole module — with waqf/pause/ayah-end
// marks already excluded (newWordsInAyah only returns proper words).
// LearnSightVocabulary just iterates whatever `items` it's handed, so
// feeding it this synthetic list reuses the component completely unchanged.
function newWordItems(m, hasSections, selectedSection) {
  const allAyahs = (m.passages || []).flatMap((p) => p.ayahs || []);
  const sectionAyahs = hasSections
    ? allAyahs.filter((a) => (a.rule || String(a.n)) === selectedSection)
    : allAyahs;
  const seen = new Set();
  const out = [];
  for (const a of sectionAyahs) {
    for (const w of newWordsInAyah(a)) {
      if (!seen.has(w)) { seen.add(w); out.push({ ar: w, translit: "", rule: null }); }
    }
  }
  return out;
}

/**
 * Learn shell for module 9 — mirrors LearnMode.jsx exactly: two tabs,
 * Reading & Listening and Sight Vocabulary. Sight Vocabulary here drills
 * the newly-introduced words in the currently-selected ayah/group only
 * (not the whole module), but reuses the exact same component modules 1-7
 * use.
 */
export default function LearnModeRecording({ module: m, hasSections, selectedSection, sections, onSelectSection }) {
  const { t } = useI18n();
  const [tab, setTab] = useState("reading_listening"); // "reading_listening" | "sight_vocabulary"

  return (
    <>
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-2 flex items-center gap-2" data-testid="learn-tabs">
        <button
          data-testid="learn-tab-reading-listening"
          onClick={() => setTab("reading_listening")}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition ${
            tab === "reading_listening" ? "bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]" : "text-[#4B5563] hover:text-[#1F2937] border border-transparent"
          }`}>
          <Headphones className="w-4 h-4" /> {t("rd.learn_tab_reading_listening")}
        </button>
        <button
          data-testid="learn-tab-sight-vocabulary"
          onClick={() => setTab("sight_vocabulary")}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition ${
            tab === "sight_vocabulary" ? "bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]" : "text-[#4B5563] hover:text-[#1F2937] border border-transparent"
          }`}>
          <Sparkles className="w-4 h-4" /> {t("rd.learn_tab_sight_vocabulary")}
        </button>
      </div>

      <div className="mt-4">
        {tab === "reading_listening"
          ? <LearnReadingListeningRecording module={m} hasSections={hasSections} selectedSection={selectedSection}
              sections={sections} onSelectSection={onSelectSection} />
          : <LearnSightVocabulary module={{ ...m, items: newWordItems(m, hasSections, selectedSection) }} />}
      </div>
    </>
  );
}
